import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { logger } from '@/utils/logger';

export type RecordingStatus = 'idle' | 'recording' | 'processing' | 'transcribing' | 'completed' | 'error';

interface RecordingState {
  // State
  status: RecordingStatus;
  isRecording: boolean;
  isProcessing: boolean;
  duration: number;
  selectedTemplate: string | null;
  audioBlob: Blob | null;
  meetingId: string | null;
  error: string | null;

  // Timer interval reference (stored outside of state for performance)
  _timerInterval: NodeJS.Timeout | null;
}

interface RecordingActions {
  // Actions
  setStatus: (status: RecordingStatus) => void;
  startRecording: (template: string) => void;
  stopRecording: (audioBlob: Blob) => void;
  setProcessing: (isProcessing: boolean) => void;
  setTranscribing: (meetingId: string) => void;
  setCompleted: (meetingId: string) => void;
  setError: (error: string) => void;
  setTemplate: (templateId: string | null) => void;
  setDuration: (duration: number) => void;
  incrementDuration: () => void;
  reset: () => void;

  // Timer management
  startTimer: () => void;
  stopTimer: () => void;
}

export type RecordingStore = RecordingState & RecordingActions;

const initialState: RecordingState = {
  status: 'idle',
  isRecording: false,
  isProcessing: false,
  duration: 0,
  selectedTemplate: null,
  audioBlob: null,
  meetingId: null,
  error: null,
  _timerInterval: null,
};

export const useRecordingStore = create<RecordingStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      setStatus: (status) => {
        logger.info('[RecordingStore] Status changed:', status);
        set({ status }, false, 'setStatus');
      },

      startRecording: (template) => {
        logger.info('[RecordingStore] Starting recording with template:', template);
        set({
          status: 'recording',
          isRecording: true,
          isProcessing: false,
          selectedTemplate: template,
          duration: 0,
          audioBlob: null,
          meetingId: null,
          error: null,
        }, false, 'startRecording');

        // Start the timer
        get().startTimer();
      },

      stopRecording: (audioBlob) => {
        logger.info('[RecordingStore] Stopping recording, blob size:', audioBlob.size);
        get().stopTimer();
        set({
          status: 'processing',
          isRecording: false,
          isProcessing: true,
          audioBlob,
        }, false, 'stopRecording');
      },

      setProcessing: (isProcessing) => {
        set({
          isProcessing,
          status: isProcessing ? 'processing' : get().status,
        }, false, 'setProcessing');
      },

      setTranscribing: (meetingId) => {
        logger.info('[RecordingStore] Transcribing meeting:', meetingId);
        set({
          status: 'transcribing',
          isProcessing: true,
          meetingId,
        }, false, 'setTranscribing');
      },

      setCompleted: (meetingId) => {
        logger.info('[RecordingStore] Recording completed, meeting:', meetingId);
        set({
          status: 'completed',
          isRecording: false,
          isProcessing: false,
          meetingId,
        }, false, 'setCompleted');
      },

      setError: (error) => {
        logger.error('[RecordingStore] Error:', error);
        get().stopTimer();
        set({
          status: 'error',
          isRecording: false,
          isProcessing: false,
          error,
        }, false, 'setError');
      },

      setTemplate: (templateId) => {
        set({ selectedTemplate: templateId }, false, 'setTemplate');
      },

      setDuration: (duration) => {
        set({ duration }, false, 'setDuration');
      },

      incrementDuration: () => {
        set((state) => ({ duration: state.duration + 1 }), false, 'incrementDuration');
      },

      reset: () => {
        logger.info('[RecordingStore] Resetting store');
        get().stopTimer();
        set({ ...initialState, _timerInterval: null }, false, 'reset');
      },

      startTimer: () => {
        const state = get();
        if (state._timerInterval) {
          clearInterval(state._timerInterval);
        }

        const interval = setInterval(() => {
          get().incrementDuration();
        }, 1000);

        set({ _timerInterval: interval }, false, 'startTimer');
      },

      stopTimer: () => {
        const state = get();
        if (state._timerInterval) {
          clearInterval(state._timerInterval);
          set({ _timerInterval: null }, false, 'stopTimer');
        }
      },
    })),
    { name: 'recording-store' }
  )
);

// Selector hooks for optimized re-renders
export const useRecordingStatus = () => useRecordingStore((state) => state.status);
export const useIsRecording = () => useRecordingStore((state) => state.isRecording);
export const useRecordingDuration = () => useRecordingStore((state) => state.duration);
export const useSelectedTemplate = () => useRecordingStore((state) => state.selectedTemplate);

// Format duration helper
export const formatRecordingDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
